/**
 * Query keys for the whole app, in one place.
 *
 * Keeping them here means an invalidation can target a whole branch (`keys.graphs.all`) without
 * a caller having to guess how a key was spelled somewhere else.
 */
export const keys = {
    celonis: {
        all: ["celonis"] as const,
        datapools: () => [...keys.celonis.all, "datapools"] as const,
        datamodels: (datapoolId?: string | null) =>
            [...keys.celonis.all, "datamodels", datapoolId] as const,
        activities: (datapoolId?: string | null, datamodelId?: string | null) =>
            [...keys.celonis.all, "activities", datapoolId, datamodelId] as const,
        tables: (datapoolId?: string | null, datamodelId?: string | null) =>
            [...keys.celonis.all, "tables", datapoolId, datamodelId] as const,
        caseTables: (datapoolId?: string | null, datamodelId?: string | null) =>
            [...keys.celonis.all, "case-tables", datapoolId, datamodelId] as const,
        activityTables: (datapoolId?: string | null, datamodelId?: string | null) =>
            [...keys.celonis.all, "activity-tables", datapoolId, datamodelId] as const,
        columns: (datapoolId?: string | null, datamodelId?: string | null, tableId?: string | null) =>
            [...keys.celonis.all, "columns", datapoolId, datamodelId, tableId] as const,
        templates: () => [...keys.celonis.all, "templates"] as const,
    },
    workspaces: {
        all: ["workspaces"] as const,
        tree: () => [...keys.workspaces.all, "tree"] as const,
        permissions: (workspaceId: number) =>
            [...keys.workspaces.all, workspaceId, "permissions"] as const,
        userPermissions: (workspaceId: number) =>
            [...keys.workspaces.all, workspaceId, "permissions", "user"] as const,
        results: (workspaceId: number) => [...keys.workspaces.all, workspaceId, "results"] as const,
        missingTrainingProcesses: () => [...keys.workspaces.all, "missing-training-processes"] as const,
    },
    graphs: {
        all: ["graphs"] as const,
        detail: (graphId?: number | null) => [...keys.graphs.all, graphId] as const,
        versions: (graphId?: number | null) => [...keys.graphs.all, graphId, "versions"] as const,
        lock: (graphId?: number | null) => [...keys.graphs.all, graphId, "lock"] as const,
        permissions: (graphId?: number | null) => [...keys.graphs.all, graphId, "permissions"] as const,
        userPermissions: (graphId?: number | null) =>
            [...keys.graphs.all, graphId, "permissions", "user"] as const,
        order: (workspaceId?: number | null) => [...keys.graphs.all, "order", workspaceId] as const,
        exercise: (graphId?: number | null) => [...keys.graphs.all, graphId, "exercise"] as const,
        jobExecutions: (graphId?: number | null, page?: { count: number; from: number }) =>
            [...keys.graphs.all, graphId, "job-executions", page] as const,
        jobExecutionCount: (graphId?: number | null) =>
            [...keys.graphs.all, graphId, "job-executions", "count"] as const,
    },
    studio: {
        all: ["studio"] as const,
        spaces: () => [...keys.studio.all, "spaces"] as const,
        emsUrl: () => [...keys.studio.all, "ems-url"] as const,
        celonisApiStatus: () => [...keys.studio.all, "celonis-api"] as const,
    },
    users: {
        all: ["users"] as const,
        list: () => [...keys.users.all, "list"] as const,
        groups: () => [...keys.users.all, "groups"] as const,
        roles: () => [...keys.users.all, "roles"] as const,
        permissions: () => [...keys.users.all, "permissions"] as const,
        preferences: () => [...keys.users.all, "preferences"] as const,
        auditLogs: (page?: { count: number; from: number }) =>
            [...keys.users.all, "audit-logs", page] as const,
        auditLogCount: () => [...keys.users.all, "audit-logs", "count"] as const,
    },
    monitoring: {
        all: ["monitoring"] as const,
        slaConfig: () => [...keys.monitoring.all, "sla-config"] as const,
        metrics: (params: Record<string, unknown>) => [...keys.monitoring.all, "metrics", params] as const,
        incidents: (params: Record<string, unknown>) =>
            [...keys.monitoring.all, "incidents", params] as const,
    },
    chat: {
        all: ["chat"] as const,
        enabled: () => [...keys.chat.all, "enabled"] as const,
        conversations: (graphId?: number | null) =>
            [...keys.chat.all, "conversations", graphId] as const,
        conversation: (graphId?: number | null, conversationId?: number | null) =>
            [...keys.chat.all, "conversations", graphId, conversationId] as const,
        grammarExamples: () => [...keys.chat.all, "grammar-examples"] as const,
        adminConversations: () => [...keys.chat.all, "admin-conversations"] as const,
    },
} as const;
